/**
 * `strategiesDetail` namespace — Korean translations.
 *
 * Mirrors `en/strategiesDetail.ts` exactly; the `Record<StrategiesDetailKey, string>`
 * annotation makes a missing or misspelled key a compile error, so this file cannot
 * drift from the canonical English source. Concise 명사형/합니다체 for UI chrome.
 *
 * Deliberately English (natural to a KR trader/developer): 지표명(RSI/SMA/EMA/ROC/FGI),
 * P&L, AI, Schema. Raw DSL enum values (mode/role/scope/state/side/verdict)
 * are rendered as-is by the components next to these labels.
 */
import type { StrategiesDetailKey } from "../en/strategiesDetail";

export const strategiesDetail: Record<StrategiesDetailKey, string> = {
  // ── DSL config viewer · fallback ──
  rawTitle: "전략 정의",
  rawSubtitle: "원본 문서 (검증 실패)",

  // ── DSL config viewer · metadata ──
  definitionTitle: "정의",
  definitionSubtitle: "Schema v{version} · {id}",
  fieldName: "이름",
  fieldRiskLevel: "리스크 수준",
  fieldAuthor: "작성자",
  fieldTags: "태그",

  // ── DSL config viewer · schedule / universe / rules ──
  scheduleTitle: "스케줄",
  universeTitle: "유니버스",
  universeSubtitle: "거래 종목 {count}개",
  rulesTitle: "규칙",
  rulesSubtitle: "트리거 규칙 {count}개",
  ruleOnce: "1회 · {scope}",
  ruleCooldown: "쿨다운 {seconds}초",
  ruleWhen: "조건",
  ruleThen: "실행",

  // ── DSL config viewer · risk policy ──
  riskTitle: "리스크",
  riskSubtitle: "예산 및 가드레일 (§0.5)",
  fieldBudget: "예산",
  fieldMaxPositionValue: "최대 포지션 가치",
  fieldMaxPositionPct: "최대 포지션 %",
  fieldDailyLossCap: "일일 손실 한도",
  fieldCooldown: "쿨다운",
  fieldMaxOpenOrders: "최대 미체결 주문 수",
  fieldHaltOnBreach: "위반 시 중단",
  yes: "예",
  no: "아니요",

  // ── DSL config viewer · execution policy ──
  executionTitle: "실행",
  executionSubtitle: "모드: {mode}",
  executionAuto:
    "규칙이 발동되면 주문이 자동으로 실행됩니다 (모의 실행 및 전역 실거래 스위치는 계속 적용됩니다).",
  fieldApprovalTimeout: "승인 타임아웃",
  fieldOnTimeout: "타임아웃 시",
  fieldModel: "모델",
  fieldTimeout: "타임아웃",
  fieldCanVetoResize: "거부/재조정 권한",
  advisorVeto: "거부",
  advisorResize: "재조정",
  fieldMaxResizeDown: "최대 하향 재조정",
  fieldPromptContext: "프롬프트 컨텍스트",
  fieldAdvisorInstructions: "어드바이저 지침",
  advisorInstructionsShowMore: "전체 지침 보기",
  advisorInstructionsShowLess: "접기",

  // ── DSL config viewer · notifications ──
  notificationsTitle: "알림",
  notificationsSubtitle: "전략별 채널 라우팅",
  noChannels: "설정된 채널 없음",

  // ── decision trace ──
  decisionTraceTitle: "의사결정 추적",
  decisionTraceEmpty: "이 실행에 대해 기록된 의사결정 항목이 없습니다.",
  decisionTraceSubtitle: "감사 단계 {count}개",
  blockIndicators: "지표",
  blockConditionTrace: "조건 추적",
  blockRiskChecks: "리스크 검사",
  checkPass: "통과",
  checkFail: "실패",
  blockAiVerdict: "AI 어드바이저 판정",
  blockOrderIntent: "주문 의도",
  intentHighValue: "고액 · 승인",
  decisionRowEmpty: "이 단계에 대해 기록된 구조화 입력이 없습니다.",

  // ── run-history timeline ──
  runHistoryTitle: "실행 이력",
  runHistorySubtitle: "모든 틱 — 시그널, 차단, 체결",
  runHistoryError: "실행 이력을 불러오지 못했습니다.",
  runHistoryEmpty: "아직 실행 기록이 없습니다. 이 전략은 아직 틱을 평가하지 않았습니다.",
  loadOlderRuns: "이전 실행 더 보기",
  viewAllRuns: "전체 실행 이력 보기",

  // ── strategy portfolio (ledger-scoped) panel ──
  portfolioTitle: "전략 포지션",
  portfolioSubtitle: "원장 기준 장부 — 리스크 게이트가 실제로 보는 값",
  portfolioError: "전략 포트폴리오를 불러오지 못했습니다.",
  portfolioValueLabel: "전략 자산",
  portfolioAllocated: "투입 금액",
  portfolioUnrealized: "미실현 손익",
  portfolioRealizedToday: "오늘 실현",
  portfolioColSymbol: "종목",
  portfolioColQty: "수량",
  portfolioColSellable: "매도 가능",
  portfolioColValue: "평가액",
  portfolioColPnlPct: "손익 %",
  portfolioUnknown: "알 수 없음",
  portfolioAllFlat:
    "전략 원장에 보유 포지션이 없습니다 — 계좌의 외부 보유분은 의도적으로 표시되지 않습니다.",

  // ── strategy P&L panel ──
  performanceTitle: "성과",
  performanceSubtitle: "실현 P&L, 승률, 손익비",
  performanceError: "성과를 불러오지 못했습니다.",
  statRealizedPnl: "실현 손익",
  statTrades: "거래 {count}건",
  statWinRate: "승률",
  statWinLoss: "{wins}승 / {losses}패",
  statPayoffRatio: "손익비",
  statPayoffCaption: "평균 수익 / 평균 손실",
  statTradesLabel: "거래",
  statTradesCaption: "청산된 포지션",
  profit: "{amount} 수익",
  loss: "{amount} 손실",
};
